/**-----------------------------------------------------------------------------------------
* Copyright © 2023 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the package root for more information
*-------------------------------------------------------------------------------------------*/
"use client";
import * as n from "react";
import t from "prop-types";
import { validatePackage as C, useDir as w, classNames as h, kendoThemeMaps as P } from "@progress/kendo-react-common";
const M = {
  name: "@progress/kendo-react-indicators",
  productName: "KendoReact",
  productCodes: ["KENDOUIREACT", "KENDOUICOMPLETE"],
  publishDate: 1702644804,
  version: "",
  licensingDocsUrl: "https://www.telerik.com/kendo-react-ui/components/my-license/"
}, N = n.forwardRef((e, m) => {
  C(M);
  const {
    children: i,
    className: c,
    style: l
  } = e, s = n.useRef(null), r = n.useCallback(
    () => {
      s.current && s.current.focus();
    },
    []
  );
  n.useImperativeHandle(m, () => ({
    element: s.current,
    focus: r
  }));
  const o = w(s, e.dir), a = n.useMemo(
    () => e.size === null ? null : e.size || f.size,
    [e.size]
  ), d = n.useMemo(
    () => e.fillMode === null || e.fill === null ? null : e.fill || e.fillMode || f.fillMode,
    [e.fillMode, e.fill]
  ), k = n.useMemo(
    () => e.themeColor || f.themeColor,
    [e.themeColor]
  ), g = n.useMemo(
    () => {
      if (e.rounded !== void 0)
        return e.rounded || "medium";
      if (e.shape !== void 0)
        switch (e.shape) {
          case "rounded":
            return "md";
          case "rectangle":
            return null;
          default:
            return "full";
        }
      return "medium";
    },
    [e.rounded, e.shape]
  ), {
    cutoutBorder: y = f.cutoutBorder,
    position: b = f.position,
    align: u = f.align
  } = e, T = n.useMemo(
    () => h(
      "k-badge",
      {
        "k-badge-sm": a === "small",
        "k-badge-md": a === "medium",
        "k-badge-lg": a === "large",
        [`k-badge-${d}`]: d,
        [`k-badge-${d}-${k}`]: !!(d && k),
        [`k-rounded-${P.roundedMap[g] || g}`]: g,
        "k-badge-border-cutout": y,
        [`k-badge-${b}`]: b,
        "k-top-start": u.vertical === "top" && u.horizontal === "start",
        "k-top-end": u.vertical === "top" && u.horizontal === "end",
        "k-bottom-start": u.vertical === "bottom" && u.horizontal === "start",
        "k-bottom-end": u.vertical === "bottom" && u.horizontal === "end"
      },
      c
    ),
    [
      a,
      d,
      k,
      g,
      y,
      u,
      b,
      c
    ]
  );
  return /* @__PURE__ */ n.createElement("span", { className: T, style: l, dir: o }, i);
});
N.propTypes = {
  className: t.string,
  dir: t.string,
  style: t.object,
  align: t.shape({
    vertical: t.oneOf(["top", "bottom"]),
    horizontal: t.oneOf(["start", "end"])
  }),
  size: t.oneOf([null, "small", "medium", "large"]),
  fillMode: t.oneOf([null, "solid", "outline"]),
  fill: t.oneOf([null, "solid", "outline"]),
  themeColor: t.oneOf([
    null,
    "primary",
    "secondary",
    "tertiary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
    "inverse",
    "inherit"
  ]),
  shape: t.oneOf(["circle", "rectangle", "rounded", "dot", "pill"]),
  rounded: t.oneOf([null, "small", "medium", "large", "full"]),
  position: t.oneOf(["edge", "outside", "inside"]),
  cutoutBorder: t.bool
};
const f = {
  size: "medium",
  fillMode: "solid",
  themeColor: "primary",
  position: "edge",
  align: { vertical: "top", horizontal: "end" },
  cutoutBorder: !1
};
N.defaultProps = f;
N.displayName = "KendoBadge";
const R = n.forwardRef((e, m) => {
  const {
    children: i,
    className: c,
    style: l
  } = e, s = n.useRef(null), r = n.useCallback(
    () => {
      s.current && s.current.focus();
    },
    []
  );
  n.useImperativeHandle(m, () => ({
    element: s.current,
    focus: r
  }));
  const o = w(s, e.dir), a = n.useMemo(
    () => h(
      "k-badge-container",
      c
    ),
    [c]
  ), d = n.useMemo(
    () => ({
      display: "inline-block",
      ...l
    }),
    [l]
  );
  return /* @__PURE__ */ n.createElement("span", { className: a, style: d, dir: o }, i);
});
R.propTypes = {
  className: t.string,
  dir: t.string,
  style: t.object
};
R.displayName = "KendoBadgeContainer";
const O = {
  pulsing: 2,
  "infinite-spinner": 3,
  "converging-spinner": 4
}, B = {
  pulsing: "k-loader-pulsing-2",
  "infinite-spinner": "k-loader-spinner-3",
  "converging-spinner": "k-loader-spinner-4"
}, z = n.forwardRef((e, m) => {
  C(M);
  const {
    className: i,
    style: c
  } = e, l = n.useRef(null), s = n.useCallback(
    () => {
      l.current && l.current.focus();
    },
    []
  );
  n.useImperativeHandle(m, () => ({
    element: l.current,
    focus: s
  }));
  const r = n.useMemo(
    () => e.size || p.size,
    [e.size]
  ), o = n.useMemo(
    () => e.themeColor || p.themeColor,
    [e.themeColor]
  ), a = n.useMemo(
    () => e.type || p.type,
    [e.type]
  ), d = n.useMemo(
    () => h(
      "k-loader",
      {
        "k-loader-sm": r === "small",
        "k-loader-md": r === "medium",
        "k-loader-lg": r === "large",
        "k-loader-primary": o === "primary",
        "k-loader-secondary": o === "secondary",
        "k-loader-tertiary": o === "tertiary",
        "k-loader-info": o === "info",
        "k-loader-success": o === "success",
        "k-loader-warning": o === "warning",
        "k-loader-error": o === "error",
        "k-loader-dark": o === "dark",
        "k-loader-light": o === "light",
        "k-loader-inverse": o === "inverse"
      },
      i,
      B[a]
    ),
    [
      r,
      o,
      i,
      a
    ]
  ), k = new Array(O[a]);
  return k.fill(0, 0, O[a]), /* @__PURE__ */ n.createElement("div", { className: d, style: c }, /* @__PURE__ */ n.createElement("div", { className: "k-loader-canvas" }, k.map((g, y) => /* @__PURE__ */ n.createElement("span", { key: y, className: "k-loader-segment" }))));
});
z.propTypes = {
  className: t.string,
  style: t.object,
  type: t.oneOf([
    "pulsing",
    "infinite-spinner",
    "converging-spinner"
  ]),
  size: t.oneOf(["small", "medium", "large"]),
  themeColor: t.oneOf([
    "primary",
    "secondary",
    "tertiary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
    "inverse"
  ])
};
const p = {
  size: "medium",
  themeColor: "primary",
  type: "pulsing"
};
z.defaultProps = p;
z.displayName = "KendoLoader";
const E = n.forwardRef((e, m) => {
  C(M);
  const {
    className: i,
    style: c
  } = e, l = n.useRef(null), s = n.useCallback(
    () => {
      l.current && l.current.focus();
    },
    []
  );
  n.useImperativeHandle(m, () => ({
    element: l.current,
    focus: s
  }));
  const r = n.useMemo(
    () => e.shape || v.shape,
    [e.shape]
  ), o = n.useMemo(
    () => e.animation === !1 ? !1 : e.animation || v.animation,
    [e.animation]
  ), a = n.useMemo(
    () => h(
      "k-skeleton",
      {
        "k-skeleton-circle": r === "circle",
        "k-skeleton-rect": r === "rectangle",
        "k-skeleton-text": r === "text",
        "k-skeleton-pulse": typeof o == "object" && o.type === "pulse",
        "k-skeleton-wave": typeof o == "object" && o.type === "wave" || o === !0
      },
      i
    ),
    [r, o, i]
  );
  return /* @__PURE__ */ n.createElement(
    "span",
    {
      ref: l,
      className: a,
      style: c
    }
  );
});
E.propTypes = {
  className: t.string,
  style: t.object,
  animation: t.shape({
    type: t.oneOf(["wave", "pulse"])
  }),
  shape: t.oneOf(["circle", "rectangle", "text"])
};
const v = {
  shape: "text",
  animation: { type: "pulse" }
};
E.defaultProps = v;
E.displayName = "KendoSkeleton";
export {
  N as Badge,
  R as BadgeContainer,
  z as Loader,
  E as Skeleton
};
